"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Recorder = void 0;
const tslib_1 = require("tslib");
const lib_1 = require("@video/log-client/lib");
const adapter_1 = require("../api/adapter");
const errors_deprecated_1 = require("../internal/errors-deprecated");
const event_emitter_1 = require("../internal/utils/events/event-emitter");
const package_json_1 = tslib_1.__importDefault(require("../package-json"));
const common_1 = require("../utils/common");
class Recorder extends event_emitter_1.ObservableEventEmitter {
    constructor(stream, options) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        super();
        this.recorder = null;
        this.locked = false;
        this.destroyed = false;
        this.localFilePath = null;
        this.writable = null;
        this.totalStorageAvailable = 0;
        this.maxRecordingLength = 0;
        this.dataWritten = false;
        this.closing = false;
        this.saving = false;
        this.storageAlertSent = false;
        this.userClosedSave = false;
        this.dataAggregate = [];
        if (!adapter_1.device.isImplements(adapter_1.Feature.MEDIA_RECORDER)) {
            this.throwErrorDeprecated(new errors_deprecated_1.NotSupportedError("MediaRecorder is not supported", {}));
        }
        if (!adapter_1.device.isImplements(adapter_1.Feature.MEDIA_STREAM) || !adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE)) {
            this.throwErrorDeprecated(new errors_deprecated_1.NotSupportedError("MediaStream is not supported", {}));
        }
        if (!adapter_1.device.isImplements(adapter_1.Feature.AUDIO_CONTEXT)) {
            this.throwErrorDeprecated(new errors_deprecated_1.NotSupportedError("AudioContext is not supported", {}));
        }
        this.device = adapter_1.device;
        this.logger =
            (_a = options.logger) !== null && _a !== void 0 ? _a : new lib_1.LoggerCore(common_1.PACKAGE_NAME)
                .setLoggerMeta("client", "VDC")
                .setLoggerMeta("release", package_json_1.default.version)
                .appendChain(Recorder);
        this.on("error", (err) => {
            errors_deprecated_1.VideoClientErrorDeprecated.log(err, this.logger);
        });
        // Mediastream
        this.stream = stream !== null && stream !== void 0 ? stream : null;
        this.options = options !== null && options !== void 0 ? options : {};
        // Default options
        this.options.mimetype = (_b = this.options.mimetype) !== null && _b !== void 0 ? _b : (adapter_1.device.isIosDevice ? "video/mp4" : "video/webm;codecs=VP8");
        this.options.startRecordingWhenClicked = (_c = this.options.startRecordingWhenClicked) !== null && _c !== void 0 ? _c : true;
        const audioBitsPerSecond = (_d = this.options.audioBitsPerSecond) !== null && _d !== void 0 ? _d : 128000;
        this.options.audioBitsPerSecond = audioBitsPerSecond;
        const videoBitsPerSecond = (_e = this.options.videoBitsPerSecond) !== null && _e !== void 0 ? _e : 5000000;
        this.options.videoBitsPerSecond = videoBitsPerSecond;
        this.options.cursor = (_f = this.options.cursor) !== null && _f !== void 0 ? _f : "motion";
        this.options.displaySurface = (_g = this.options.displaySurface) !== null && _g !== void 0 ? _g : "all";
        this.options.frameRate = (_h = this.options.frameRate) !== null && _h !== void 0 ? _h : 24;
        this.options.height = 1920;
        if (!adapter_1.device.MediaRecorder.isTypeSupported(this.options.mimetype)) {
            this.throwErrorDeprecated(new errors_deprecated_1.MediaRecorderError("mimeType is not supported", { mimeType: this.options.mimetype }));
        }
        (_k = (_j = this.device).storageEstimate) === null || _k === void 0 ? void 0 : _k.call(_j).then(({ usage, quota }) => {
            const usageInMib = Math.round(usage / (1024 * 1024));
            const quotaInMib = Math.round(quota / (1024 * 1024));
            const availableMbs = quotaInMib - usageInMib;
            this.totalStorageAvailable = availableMbs;
            // Calculate megabytes per second usage of recorder based on recording options
            const mbpsPerSecond = (audioBitsPerSecond + videoBitsPerSecond) / 8 / 1000000;
            // Calculate max recording length in minutes. Subtract 1 minute for buffer.
            this.maxRecordingLength = availableMbs / (mbpsPerSecond * 60) - 1;
        });
    }
    static isSupported(mimetype) {
        return adapter_1.device.isImplements(adapter_1.Feature.MEDIA_RECORDER) && adapter_1.device.MediaRecorder.isTypeSupported(mimetype);
    }
    /**
     * private function designed to setup a generic recorder with its events
     */
    newMediaRecorder(stream, options) {
        const mr = new this.device.MediaRecorder(stream, options);
        mr.addEventListener("error", (err) => {
            this.emitErrorDeprecated(new errors_deprecated_1.MediaRecorderError("media recorder error", {}));
        });
        const vt = stream.getVideoTracks();
        if (vt.length === 0 || !vt[0].enabled || vt[0].readyState === "ended") {
            this.throwErrorDeprecated(new errors_deprecated_1.MediaRecorderError("no active video tracks", {}));
        }
        // Handles when user clicks chrome "stop sharing"
        vt[0].addEventListener("ended", () => {
            this.emit("browser-stop");
        });
        mr.addEventListener("pause", this.handlePause);
        mr.addEventListener("resume", this.handleResume);
        mr.addEventListener("start", this.handleStart);
        mr.addEventListener("stop", this.handleStop);
        mr.addEventListener("dataavailable", this.handleNewData);
        return mr;
    }
    handlePause() {
        this.emit("pause");
    }
    handleResume() {
        this.emit("resume");
    }
    handleStart() {
        this.emit("start");
    }
    handleStop() {
        this.emit("stop");
    }
    async handleNewData(ev) {
        var _a, _b;
        if (this.closing) {
            return;
        }
        if (this.saving) {
            const options = {
                suggestedName: `recording.${Date.now()}`,
                types: [
                    {
                        description: "Video Recording",
                        accept: {
                            "video/x-matroska": [".webm"],
                        },
                        "-use_wallclock_as_timestamps": true,
                    },
                ],
            };
            try {
                this.localFilePath = await this.device.showSaveFilePicker(options);
                this.writable = await this.localFilePath.createWritable();
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.MediaRecorderError("an error occurred on save record", { inner }));
                this.saving = false;
                this.dataAggregate.push(ev.data);
                this.emit("save-canceled");
                return;
            }
            this.emit("data", ev.data);
            let blob = null;
            if (this.dataAggregate.length > 0) {
                this.dataAggregate.push(ev.data);
                blob = new Blob(this.dataAggregate);
            }
            else {
                blob = new Blob([ev.data]);
            }
            try {
                await ((_a = this.writable) === null || _a === void 0 ? void 0 : _a.write(blob));
                this.dataWritten = true;
                (_b = this.writable) === null || _b === void 0 ? void 0 : _b.close();
                this.dataAggregate = [];
                await this.close();
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.MediaRecorderError("failed to write to file", { inner }));
            }
            return;
        }
        if (!this.dataWritten && !this.storageAlertSent) {
            this.storageAlertSent = true;
            this.pause();
            this.dataAggregate.push(ev.data);
            this.device.confirmMessage("Your device is nearing its storage capacity. Please return to the app to save your recording.");
            await this.record();
        }
    }
    /**
     * private helpers to lock device retrieval and local stream setup
     */
    lock() {
        this.locked = true;
        this.emit("lock");
    }
    unlock() {
        this.locked = false;
        this.emit("unlock");
    }
    /**
     * @method apply reload the recorder to reproduce a new keyframe, do this by retrieving devices again
     * @param {object} options audio & video bits per second
     * */
    apply(options) {
        if (this.stream == null) {
            return;
        }
        if (this.locked) {
            this.logger.debug("recorder apply locked");
            adapter_1.device.setTimeout(() => {
                this.apply(options);
            }, 50);
            return;
        }
        this.lock();
        this.logger.info("applying recorder options", {
            options,
        });
    }
    /**
     * @property the current state of the stream: [inactive, recording, paused]
     */
    get state() {
        var _a, _b;
        return (_b = (_a = this.recorder) === null || _a === void 0 ? void 0 : _a.state) !== null && _b !== void 0 ? _b : "inactive";
    }
    /**
     * @property the current recorder options
     */
    get recorderOptions() {
        return {
            audioBitsPerSecond: this.options.audioBitsPerSecond,
            videoBitsPerSecond: this.options.videoBitsPerSecond,
            mimetype: this.options.mimetype,
            startRecordingWhenClicked: this.options.startRecordingWhenClicked,
            frameRate: this.options.frameRate,
            displaySurface: this.options.displaySurface,
            cursor: this.options.cursor,
            height: this.options.height,
        };
    }
    async setupRecorder() {
        let stream;
        if (this.stream == null) {
            stream = await this.createStream();
        }
        else {
            stream = this.stream;
        }
        this.dataWritten = false;
        this.closing = false;
        this.saving = false;
        if (this.recorder == null) {
            this.recorder = await this.newMediaRecorder(stream, this.recorderOptions);
        }
        if (this.options.startRecordingWhenClicked) {
            await this.record();
        }
        return {
            status: "success",
        };
    }
    /**
     * @method record starts or resumes recording, emits an error if there is no active stream
     */
    async record() {
        var _a, _b;
        if (this.stream == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.MediaRecorderError("attempting to record without a local stream", {}));
            return {
                status: "error",
                message: "No stream provided or created",
            };
        }
        if (this.stream.getTracks().length === 0) {
            this.emit("stream", null);
            this.emitErrorDeprecated(new errors_deprecated_1.MediaRecorderError("attempting to start to record with a local stream that has no tracks", {}));
            return {
                status: "error",
                message: "No video tracks from the stream found",
            };
        }
        if (this.state === "inactive") {
            // Start recorder with timeInterval of maxRecordingLength and convert to milliseconds by multiplying by 60000
            (_a = this.recorder) === null || _a === void 0 ? void 0 : _a.start(this.maxRecordingLength * 60000);
        }
        else if (this.state === "paused") {
            (_b = this.recorder) === null || _b === void 0 ? void 0 : _b.resume();
        }
        return {
            status: "success",
        };
    }
    /**
     * @method pause pauses a recording, emits an error if a recording is not already happening
     */
    async save() {
        if (this.recorder == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.MediaRecorderError("attempting to pause recorder with no recorder", {}));
            return {
                status: "error",
                message: "No recorder found",
            };
        }
        this.saving = true;
        this.pause();
        await this.recorder.requestData();
        return {
            status: "success",
        };
    }
    /**
     * @method pause pauses a recording, emits an error if a recording is not already happening
     */
    pause() {
        if (this.recorder == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.MediaRecorderError("attempting to pause recorder with no recorder", {}));
            return {
                status: "error",
                message: "No recorder found",
            };
        }
        this.recorder.pause();
        return {
            status: "success",
        };
    }
    /**
     * @method close closes the recorder, emits an error if a recording is not already happening
     */
    async close() {
        var _a;
        if (this.recorder == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.MediaRecorderError("attempting to stop recorder with no recorder", {}));
            return {
                status: "error",
                message: "No recorder found",
            };
        }
        if (this.recorder.state !== "inactive") {
            this.closing = true;
            try {
                // Close all tracks on streams
                const tracks = (_a = this.stream) === null || _a === void 0 ? void 0 : _a.getTracks();
                tracks === null || tracks === void 0 ? void 0 : tracks.forEach((track) => {
                    track.stop();
                });
                // Stop the recorder
                this.recorder.stop();
                // Set state to be a new recorder
                this.recorder = null;
                this.destroyed = true;
                this.stream = null;
                this.localFilePath = null;
                this.writable = null;
                // this.removeAllListeners();
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.MediaRecorderError("failed to close", { inner }));
                return {
                    status: "error",
                    message: "Failed to close",
                };
            }
        }
        else {
            // return this.emit("error", "recorder already closed");
        }
        return {
            status: "success",
        };
    }
    async createStream() {
        const mediaTrackConstraints = {
            video: {
                frameRate: this.options.frameRate,
                height: this.options.height,
                resizeMode: "crop-and-scale",
                cursor: this.options.cursor,
                displaySurface: this.options.displaySurface,
                logicalSurface: true,
            },
            audio: {
                autoGainControl: false,
                echoCancellation: false,
                googAutoGainControl: false,
                noiseSuppression: false,
            },
        };
        const videoMediaStream = await this.device.mediaDevices.getDisplayMedia(mediaTrackConstraints);
        const micAudioStream = await this.device.mediaDevices.getUserMedia({ audio: true });
        const audioTracks = videoMediaStream.getAudioTracks();
        const videoStream = videoMediaStream.getVideoTracks();
        const audioCtx = new this.device.AudioContext();
        let micAudioTrack;
        let tabAudioTrack;
        let dst;
        const mixedStream = new this.device.MediaStream();
        let tabAudioStream;
        if (audioTracks.length > 0) {
            tabAudioTrack = videoMediaStream.getAudioTracks()[0];
            dst = audioCtx.createMediaStreamDestination();
            tabAudioStream = new this.device.MediaStream([tabAudioTrack]);
            audioCtx.createMediaStreamSource(tabAudioStream).connect(dst);
            audioCtx.createMediaStreamSource(micAudioStream).connect(dst);
            mixedStream.addTrack(dst.stream.getTracks()[0]);
            mixedStream.addTrack(videoStream[0]);
        }
        else {
            micAudioTrack = micAudioStream.getAudioTracks();
            mixedStream.addTrack(micAudioTrack[0]);
            mixedStream.addTrack(videoStream[0]);
        }
        this.stream = mixedStream;
        return mixedStream;
    }
    /**
     * @method addAudioTrack Adds an audio track to the stream
     */
    addAudioTrack(audioTrack) {
        var _a;
        (_a = this.stream) === null || _a === void 0 ? void 0 : _a.addTrack(audioTrack);
    }
    /**
     * @method removeAudioTrack removes an audio track from the stream
     */
    removeAudioTrack(audioTrack) {
        var _a;
        const audioTracks = (_a = this.stream) === null || _a === void 0 ? void 0 : _a.getAudioTracks();
        audioTracks === null || audioTracks === void 0 ? void 0 : audioTracks.forEach((track) => {
            var _a;
            if (track.id === audioTrack.id) {
                (_a = this.stream) === null || _a === void 0 ? void 0 : _a.removeTrack(track);
            }
        });
    }
}
exports.Recorder = Recorder;
Recorder.displayName = "Recorder";
//# sourceMappingURL=index.js.map